import { FC, useEffect, useRef, useState } from 'react';

import { useLogin } from '@app/hooks/useLogin';
import { RoutePath } from '@app/routes';
import { Link } from '@components/Link';
import { Logo } from '@components/Logo';
import { baseStyles } from '@components/Table/util/style-util';
import { semanticMap } from '@components/Theme/newBaseColors';
import { TenantLogo } from '@connect-carrier/components/TenantLogo';
import { getSelectedCustomer } from '@connect-carrier/harness/customer';
import { useAuthHeader } from '@connect-carrier/hooks/useAuthHeader';
import { useCompanyInfo } from '@connect-carrier/hooks/useCompanyInfo';
import { fetchCustomerImageList } from '@connect-carrier/hooks/useFetchCustomerImageList';
import {
  CustomerListOutput,
  useFetchCustomerList,
} from '@connect-carrier/hooks/useFetchCustomerList';
import {
  Roles,
  useHasAnyRole,
  useHasRole,
} from '@connect-carrier/hooks/useHasRole';
import { CustomerList } from '@connect-carrier/views/customer-landing/header/CustomerList';
import { useFlagMe478782EnableCarrierPortalMenuModalResponsiveFix } from '@generated/flags/ME-478782-enable-carrier-portal-menu-modal-responsive-fix';
import { useFlagMe480232CarrierPortalNavigationBrandingSearchFe } from '@generated/flags/ME-480232-carrier-portal-navigation-branding-search-fe';
import { useClickAwayConditional } from '@hooks/useClickAwayConditional';
import { useTheme } from '@hooks/useTheme';
import { jsonStringify } from '@utils/json';
import { win } from '@utils/win';
import { useNavigate } from 'react-router-dom';
import { useScreenSizes } from '../../../hooks/useScreenSizes';

export const ProfileHeader: FC<anyOk> = () => {
  const isAdmin = useHasRole(Roles.Admin);

  const basePadding = 16;
  const { table, gray } = useTheme();
  const navigate = useNavigate();
  const { keycloak, logout } = useLogin();
  /* listofCustomer Count will come from EDGE team */
  const hasCustomers: boolean = true;
  const [showElements, setShowElements] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const updateHeader = useAuthHeader;
  const companyInfo = useCompanyInfo();
  const companyName = companyInfo?.name;
  const enableProfileMenuFix =
    useFlagMe478782EnableCarrierPortalMenuModalResponsiveFix();
  const enableNavigationBrandingSearchFe =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();
  const toggleElements = (): void => {
    setShowElements(!showElements);
    setIsClicked(!isClicked);
  };

  const handleLogoClick = (): void => {
    toggleElements();
  };
  const hasPricingRole = useHasRole(Roles.Pricing);
  const exceptPricingRole = useHasAnyRole([
    Roles.Admin,
    Roles.Accounting,
    Roles.Operations,
    Roles.ReadOnly,
  ]);

  const handleCustomerSelect = (customer: CustomerListOutput): void => {
    setSelectedCustomer(customer);
    navigate(
      hasPricingRole && !exceptPricingRole ? '/available-routes' : '/my-routes',
    );
    if (
      typeof customer.companyId === 'string' &&
      typeof customer.tenantCode === 'string'
    ) {
      updateHeader(customer.companyId, customer.tenantCode);
    }
    win.location.reload();
  };
  const containerRef = useRef<HTMLDivElement>(null);

  useClickAwayConditional({
    ref: containerRef,
    onClickAway: () => {
      setShowElements(false);
      setIsClicked(false);
    },
  });

  const { tablet, desktop } = useScreenSizes();

  const { customerList } = useFetchCustomerList();
  const [updatedCustomerList, setUpdatedCustomerList] =
    useState<CustomerListOutput[]>();
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerListOutput | null>(null);
  const previousCustomerListRef = useRef<CustomerListOutput[] | null>(null);
  const handleWrapperClick = (): void => {
    setShowElements(false);
  };

  useEffect(() => {
    const updateCustomerList = async (): Promise<void> => {
      const prev = previousCustomerListRef.current;
      if (
        customerList.length > 0 &&
        jsonStringify(customerList) !== jsonStringify(prev)
      ) {
        previousCustomerListRef.current = customerList.slice();

        const newCustomerList = await fetchCustomerImageList(
          customerList.slice(),
        );
        setUpdatedCustomerList(newCustomerList);
      }
    };

    updateCustomerList();
  }, [customerList]);

  useEffect(() => {
    if (updatedCustomerList?.length) {
      const stored = getSelectedCustomer();
      if (stored) {
        const match = updatedCustomerList.find(
          (c) => c.tenantId === stored.tenantId,
        );
        if (match) {
          setSelectedCustomer(match);
        }
      }
    }
  }, [updatedCustomerList]);
  return (
    <div ref={containerRef} css={{ width: '15%' }}>
      <div data-testid="profile-header" onClick={toggleElements}>
        <div
          css={{
            width: '48px',
            height: '48px',
            flexGrow: 0,
            borderRadius: '8px',
            backgroundColor: gray[30],
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px',
            border: isClicked ? `4px solid ${gray[80]}` : 'none',
            transition: 'border 0.3s ease',
          }}
          onClick={handleLogoClick}
        >
          <div>
            {enableNavigationBrandingSearchFe ? (
              <TenantLogo
                tenantCode={selectedCustomer?.tenantCode}
                altText={selectedCustomer?.displayName}
                color={gray.white}
                css={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '4px',
                }}
                testId="customer-profile-logo"
              />
            ) : (
              <Logo
                color={gray.white}
                css={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '4px',
                }}
              />
            )}
          </div>
        </div>
      </div>
      {showElements && (
        <>
          {enableProfileMenuFix && (
            <div
              css={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 119,
              }}
              onClick={handleWrapperClick}
              data-testid="backdrop"
            />
          )}
          <div
            css={
              enableProfileMenuFix
                ? {
                    border: `1px solid ${gray[80]}`,
                    background: table.cell.alternateBackground,
                    position: 'fixed',
                    zIndex: 120,
                    width: 'calc(100vw - 40px)',
                    maxHeight: 'calc(100vh - 90px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    boxSizing: 'border-box',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    top: '65px',
                    '@media (min-width: 721px)': {
                      width: '253px',
                      left: 'auto',
                      transform: 'none',
                      right: '48px',
                      maxWidth: 'calc(100vw - 96px)',
                    },
                    '@media (min-width: 1280px)': {
                      right: '56px',
                      top: '70px',
                      maxWidth: 'calc(100vw - 112px)',
                    },
                  }
                : {
                    border: `1px solid ${gray[80]}`,
                    background: table.cell.alternateBackground,
                    position: 'absolute',
                    zIndex: 1,
                    width: desktop ? '20%' : tablet ? '28%' : '100%',
                    left: desktop ? '88%' : tablet ? '83%' : '0',
                    transform: desktop ? 'translateX(-50%)' : 'none',
                    marginTop: '18px',
                    borderRadius: '4px',
                  }
            }
            data-testid="profile-menu"
            onClick={(e) => enableProfileMenuFix && e.stopPropagation()}
          >
            <div
              css={{
                padding: basePadding,
              }}
              data-testid="profile-section"
            >
              <div
                color="text"
                css={{
                  textTransform: 'uppercase',
                  marginBottom: 10,
                  color: gray[60],
                  fontWeight: 500,
                  fontSize: 14,
                }}
                data-testid="user-profile"
              >
                User Profile
              </div>
              <strong
                css={{
                  color: gray[30],
                  fontWeight: 'bold',
                  paddingBottom: '30px',
                }}
                data-testid="customer-name"
              >
                {keycloak.idTokenParsed?.name}
              </strong>
              <div
                css={{ fontSize: 14, color: gray[60] }}
                data-testid="user-email"
              >
                {keycloak.idTokenParsed?.email}
              </div>
            </div>
            {hasCustomers && (
              <div
                css={{
                  borderTop: `1px solid ${gray[80]}`,
                  padding: baseStyles.paddingLeft,
                  borderLeftWidth: 0,
                  borderRightWidth: 0,
                }}
                data-testid="customer-section"
              >
                <h4
                  css={{
                    marginTop: 0,
                    color: gray[60],
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                  data-testid="customers-title"
                >
                  CUSTOMERS
                </h4>
                <CustomerList
                  customers={selectedCustomer ? [selectedCustomer] : []}
                  handleCustomerSelect={handleCustomerSelect}
                />
              </div>
            )}
            {isAdmin && (
              <div
                css={{
                  borderTop: `1px solid ${gray[80]}`,
                  padding: baseStyles.paddingLeft,
                }}
                data-testid="profile-account"
              >
                <h4
                  css={{
                    marginTop: 0,
                    marginBottom: 10,
                    color: gray[60],
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                  data-testid="account-title"
                >
                  ACCOUNT
                </h4>
                <div css={{ marginBottom: '5px' }}>
                  {companyName ? companyName : `Company Name`}
                </div>
                <Link
                  css={{
                    color: `${semanticMap.primary[50]} !important`,
                  }}
                  to={
                    '/company-account-settings' as RoutePath<
                      `${string}/`,
                      `${string}/`
                    >
                  }
                  data-testid="manage-settings"
                  onClick={() => {
                    sessionStorage.setItem('isCustomerSelectPage', 'true');
                    toggleElements();
                  }}
                >
                  Manage Settings
                </Link>
              </div>
            )}
            <div
              css={{
                padding: basePadding,
                borderTop: `1px solid ${gray[80]}`,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
              }}
              data-testid="sign-out"
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                logout();
              }}
            >
              <svg
                width="22"
                height="20"
                viewBox="0 0 22 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.125 5.06641V7.34375C15.125 7.94531 14.6523 8.375 14.0938 8.375H8.9375V11.125H14.0938C14.6523 11.125 15.125 11.5977 15.125 12.1562V14.4766L19.8516 9.75L15.125 5.06641ZM22 9.75C22 10.2656 21.7852 10.7383 21.4414 11.082L16.457 16.0664C16.0703 16.4531 15.5547 16.625 15.0391 16.625C13.9219 16.625 13.0625 15.7656 13.0625 14.6484V13.1875H8.9375C7.77734 13.1875 6.875 12.2852 6.875 11.125V8.375C6.875 7.25781 7.77734 6.3125 8.9375 6.3125H13.0625V4.89453C13.0625 3.77734 13.9219 2.875 15.0391 2.875C15.5547 2.875 16.0703 3.08984 16.457 3.47656L21.4414 8.46094C21.7852 8.80469 22 9.27734 22 9.75ZM7.21875 2.1875H3.78125C2.79297 2.1875 2.0625 2.96094 2.0625 3.90625V15.5938C2.0625 16.582 2.79297 17.3125 3.78125 17.3125H7.21875C7.77734 17.3125 8.25 17.7852 8.25 18.3438C8.25 18.9453 7.77734 19.375 7.21875 19.375H3.78125C1.67578 19.375 0 17.6992 0 15.5938V3.90625C0 1.84375 1.67578 0.125 3.78125 0.125H7.21875C7.77734 0.125 8.25 0.597656 8.25 1.15625C8.25 1.75781 7.77734 2.1875 7.21875 2.1875Z"
                  fill={gray[30]}
                />
              </svg>{' '}
              <span style={{ marginLeft: '8px' }}>Sign Out</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
